import React, { useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, FormGroup, Label, Input, Button } from 'reactstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import usePlacesAutocomplete, { getGeocode } from "use-places-autocomplete";
import { setFormData } from 'store/slices/flowSlice';
import { syncFormData } from 'utils/syncFormData';
import { countryOptions } from 'data/countries';
import * as Yup from 'yup';
import Select from 'react-select';
import { useTranslation } from "react-i18next";


const CustomerForm = ({ initialValues }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formData = useSelector((state) => state.flow.formData);
  const activeFlow = useSelector((state) => state.flow.activeFlow);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [error, setError] = useState(null);
  const [isValidated, setValidated] = useState(false);

  const [fullName, setFullName] = useState(initialValues.fullName);
  const [companyName, setCompanyName] = useState(initialValues.companyName);
  const [email, setEmail] = useState(initialValues.email);
  const [phoneNumber, setPhoneNumber] = useState(initialValues.phoneNumber);
  const [street, setStreet] = useState(initialValues.street);
  const [zipCode, setZipCode] = useState(initialValues.zipCode);
  const [city, setCity] = useState(initialValues.city);
  const [country, setCountry] = useState(initialValues.country);
  const [vatId, setVatId] = useState(initialValues.vatId);

  useEffect(() => {
    if (isValidated) {

      syncFormData({flow: activeFlow, ...formData}, true, (value) => {
        setError(value);
      });
    }
  }, [formData]);

  useEffect(() => {
    const matchingCountry = countryOptions.find(
      (option) => option.label === country
    );
    setSelectedCountry(matchingCountry)

  }, [country]);


  const validationSchema = Yup.object().shape({
    fullName: Yup.string()
      .required('Full name is required')
      .min(2, 'Full name must be at least 2 characters long'),

    companyName: Yup.string()
      .required('Company name is required'),

    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),

    phoneNumber: Yup.string()
      .required('Phone number is required'),

    street: Yup.string()
      .required('Street address is required'),

    zipCode: Yup.string()
      .required('Zip code is required'),

    city: Yup.string()
      .required('City is required'),

    vatId: Yup.string(),
    country: Yup.mixed()
      .required('Country is required')
      .test('is-valid-country', 'Country must be a string or an object', value => {
        return typeof value === 'string' || (typeof value === 'object' && value !== null);
      }),

    latestDeliveryDate: Yup.date()
      .min(new Date(), 'Date cannot be in the past')
      .required('Latest delivery date is required'),

    message: Yup.string()
      .max(500, 'Message cannot exceed 500 characters'),

    agreeToContact: Yup.boolean()
      .oneOf([true], 'You must agree to be contacted')
      .required('Please agree to be contacted'),

    understandBusinessPlatform: Yup.boolean()
      .oneOf([true], 'You must confirm understanding of the platform')
      .required('Please agree to the terms'),
  });


  const handleSubmit = (values) => {
    setValidated(true);
    dispatch(setFormData(values));

  };

  // Implement Google Places Autocomplete
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['address']
    },
    debounce: 300,
  });

  const handleSelectAddress = async (address, setFieldValue) => {
    try {
      const results = await getGeocode({ address });
      const addressComponents = results[0].address_components;


      // List of component types to exclude (city, state, and zip)
      const excludeTypes = ['locality', 'administrative_area_level_1', 'postal_code'];

      // Extract only the components that are not city, state, or zip code
      const filteredAddress = addressComponents
        .filter(component => !excludeTypes.some(type => component.types.includes(type)))
        .map(component => component.long_name) // Use long_name for the address
        .join(', ');
        

      const street = `${addressComponents.find(c => c.types.includes('route'))?.long_name || ''} ${addressComponents.find(c => c.types.includes('street_number'))?.long_name || ''}`;
      const city = `${addressComponents.find(c => c.types.includes('locality'))?.long_name || ''}`;
      const zipCode = `${addressComponents.find(c => c.types.includes('postal_code'))?.long_name || ''}`;
      const country = `${addressComponents.find(c => c.types.includes('country'))?.long_name || ''}`;
      const countryCode = `${addressComponents.find(c => c.types.includes('country'))?.short_name || ''}`;

      // Set the values in Formik
      setFieldValue('customerAddress', filteredAddress.trim());
      setFieldValue('street', filteredAddress.trim());
      setFieldValue('city', city);
      setFieldValue('zipCode', zipCode);
      setFieldValue('country', country);


      const matchingCountry = countryOptions.find(
        (option) => option.value === countryCode
      );
      setSelectedCountry(matchingCountry)

      setValue(address, false);
      clearSuggestions();
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  return (
    <div className="e2g-shipping-contact-us">
      <div className='e2g-shipping-contact-container container'>
        <div className="e2g-shipping-contact-form-wrap">
          <Formik
            initialValues={{
              fullName: fullName,
              companyName: companyName,
              email: email,
              phoneNumber: phoneNumber,
              street: street,
              zipCode: zipCode,
              city: city,
              vatId: vatId,
              country: country,
              latestDeliveryDate: new Date(),
              message: '',
              agreeToContact: false,
              agreeToOffers: false,
              understandBusinessPlatform: false,
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({ values, setFieldValue, errors, touched }) => (
              <Form className='e2g-form-step form-step'>
                <h4 style={{ fontSize: '32px' }}>
                  {t("Thank you for your interest, To provide you a quote tell us how to reach you?")}
                </h4>
                <div className='e2g-shipland-form w-100'>
                  <Row className="mb-1">
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.fullName ? "e2g-filled" : ""}`} for="fullName">{t("Full Name")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="fullName" type="text" as={Input} />
                        {errors.fullName && touched.fullName ? (
                          <div className="e2g-error-text text-danger">{errors.fullName}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.companyName ? "e2g-filled" : ""}`} for="companyName">{t("Company Name")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="companyName" type="text" as={Input} />
                        {errors.companyName && touched.companyName ? (
                          <div className="e2g-error-text text-danger">{errors.companyName}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.email ? "e2g-filled" : ""}`} for="email">{t("Email")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="email" type="email" as={Input} />
                        {errors.email && touched.email ? (
                          <div className="e2g-error-text text-danger">{errors.email}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row e2g-form-phone ">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.phoneNumber ? "e2g-filled" : ""}`} for="phoneNumber">{t("Phone Number")}</Label>
                        <PhoneInput
                          className='bg-light e2g-custom-height e2g-custom-fonts e2g-shipping-form-number'
                          country={'ie'}
                          value={values.phoneNumber}
                          onChange={(value, country, e, formattedValue) => setFieldValue('phoneNumber', formattedValue)}
                          inputProps={{
                            name: 'phoneNumber',
                            required: true,
                            autoFocus: true,
                            autoFormat: true,
                          }}
                        />
                        {errors.phoneNumber && touched.phoneNumber ? (
                          <div className="e2g-error-text text-danger">{errors.phoneNumber}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Address Autocomplete */}
                  <Row className="mb-1">
                    <Col sm="12">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.street ? "e2g-filled" : ""}`} for="addressAutocomplete">{t("Address")}</Label>
                        <Input
                          id="addressAutocomplete"
                          value={value}
                          name="customerAddress"
                          onChange={(e) => setValue(e.target.value)}
                          placeholder={t("Type to search...")}
                          className="bg-light e2g-custom-height e2g-form-input rounded-0 border-0"
                        />
                        {status === "OK" && (
                          <ul className="autocomplete-dropdown">
                            {data.map(({ place_id, description }) => (
                              <li
                                key={place_id}
                                onClick={() => handleSelectAddress(description, setFieldValue)}
                              >
                                {description}
                              </li>
                            ))}
                          </ul>
                        )}
                        {errors.customerAddress && touched.customerAddress ? (
                          <div className="e2g-error-text text-danger">{errors.customerAddress}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row className="mb-1">
                    <Col sm="12">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.street ? "e2g-filled" : ""}`} for="street">{t("Street")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="street" type="text" as={Input} />
                        {errors.street && touched.street ? (
                          <div className="e2g-error-text text-danger">{errors.street}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.zipCode ? "e2g-filled" : ""}`} for="zipCode">{t("Zip Code")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="zipCode" type="text" as={Input} />
                        {errors.zipCode && touched.zipCode ? (
                          <div className="e2g-error-text text-danger">{errors.zipCode}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.city ? "e2g-filled" : ""}`} for="city">{t("City")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="city" type="text" as={Input} />
                        {errors.city && touched.city ? (
                          <div className="e2g-error-text text-danger">{errors.city}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.vatId ? "e2g-filled" : ""}`} for="vatId">{t("VAT ID")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="vatId" type="text" as={Input} />
                        {errors.vatId && touched.vatId ? (
                          <div className="e2g-error-text text-danger">{errors.vatId}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group e2g-form-row">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.country ? "e2g-filled" : ""}`} for="country">{t("Country")}</Label>
                        <Select
                          className="rounded border-0 e2g-form-input"
                          id="toLocation"
                          value={selectedCountry}
                          options={countryOptions}
                          onChange={(option) => {
                            setSelectedCountry(option);
                            setFieldValue('country', option);
                          }}
                          placeholder={t("Select Country")}
                        />
                        {errors.country && touched.country ? (
                          <div className="e2g-error-text text-danger">{errors.country}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-1">
                    <Col sm="6">
                      <FormGroup className='d-flex flex-column'>
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.latestDeliveryDate ? "e2g-filled" : ""}`} for="latestDeliveryDate">{t("Latest Delivery Date")}</Label>
                        <DatePicker
                          className='e2g-ready-date  bg-light e2g-custom-height e2g-custom-fonts form-control'
                          calendarClassName="e2g-calendar"
                          id="latestDeliveryDate"
                          selected={values.latestDeliveryDate}
                          onChange={date => setFieldValue('latestDeliveryDate', date)}
                        />
                        {errors.latestDeliveryDate && touched.latestDeliveryDate ? (
                          <div className="e2g-error-text text-danger">{errors.latestDeliveryDate}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                    <Col sm="6">
                      <FormGroup className="e2g-cargo-form-group">
                        <Label className={`e2g-cargo-form-label e2g-form-label ${values.message ? "e2g-filled" : ""}`} for="message">{t("Message")}</Label>
                        <Field className='bg-light custom-height custom-fonts e2g-form-input' name="message" type="text" as={Input} />
                        {errors.message && touched.message ? (
                          <div className="e2g-error-text text-danger">{errors.message}</div>
                        ) : null}
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
                <FormGroup check className='p-0'>
                  <Label check className="d-flex align-items-center gap-3">
                    <Field type="checkbox" name="agreeToContact" />{' '}
                    <p className='mb-0'>{t("I agree to be contacted by E2G regarding my quotation request")}</p>
                  </Label>
                  {errors.agreeToContact && touched.agreeToContact ? (
                    <div className="e2g-error-text text-danger">{errors.agreeToContact}</div>
                  ) : null}
                </FormGroup>
                <FormGroup check className='p-0'>
                  <Label check className="d-flex align-items-center gap-3">
                    <Field type="checkbox" name="agreeToOffers" />{' '}
                    <p className='mb-0'>{t("I agree to be kept informed via telephone or email about dedicated offers, news, products and services as well as special offers. I can cancel my subscription at any time.")}</p>
                  </Label>
                  {errors.agreeToOffers && touched.agreeToOffers ? (
                    <div className="e2g-error-text text-danger">{errors.agreeToOffers}</div>
                  ) : null}
                </FormGroup>
                <FormGroup check className='p-0'>
                  <Label check className="d-flex align-items-center gap-3">
                    <Field type="checkbox" name="understandBusinessPlatform" />{' '}
                    <p className='mb-0'>{t("I understand that E2G land is a business-to-business platform and does not provide services for private shipper.")}</p>
                  </Label>
                  {errors.understandBusinessPlatform && touched.understandBusinessPlatform ? (
                    <div className="e2g-error-text text-danger">{errors.understandBusinessPlatform}</div>
                  ) : null}
                </FormGroup>

                <Button type="submit" color="success" className="mt-0 p-3 mb-3" style={{ width: "100%", maxWidth: "300px", margin: "0" }}>{t("Submit")}</Button>
                {error && <div className="e2g-error-text text-danger">{error}</div>}
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default CustomerForm;